import { useMemo, useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import styled from "styled-components";
import { z } from "zod";
import { validatePassword } from "../../../utils/auth";
import { Button } from "../../atoms/Button";
import { Input } from "../../atoms/Input";
import FormTemplate from "../../templates/FormTemplate";

type Props = {
  handlePasswordError: () => void;
  handleSubmit: (
    code: string,
    password: string,
    passwordConfirmation: string,
  ) => void;
};

export const ResetPasswordForm: React.FC<Props> = ({
  handlePasswordError,
  handleSubmit,
}) => {
  const [hasPasswordError, setHasPasswordError] = useState<boolean>(false);

  const schema = useMemo(() => {
    return z
      .object({
        code: z.string().min(1),
        password: z.string().min(1),
        passwordConfirmation: z.string().min(1),
      })
      .refine((data) => data.password === data.passwordConfirmation);
  }, []);

  const [methods, setMethods] = useState<UseFormReturn<
    FieldValues,
    any,
    undefined
  > | null>(null);

  return (
    <FormTemplate
      schema={schema}
      setMethods={setMethods}
      defaultValues={{
        code: "",
        password: "",
        passwordConfirmation: "",
      }}
      onSubmit={({ code, password, passwordConfirmation }) => {
        setHasPasswordError(false);

        if (validatePassword(password)) {
          handleSubmit(code, password, passwordConfirmation);
        } else {
          handlePasswordError();
          setHasPasswordError(true);
        }
      }}
    >
      <ContentWrapper>
        <Input
          name="code"
          type="text"
          label="認証コード"
          required
          helperText="メールに記載されている認証コードを入力して下さい"
          dataWovnIgnore={true}
        />
      </ContentWrapper>
      <ContentWrapper>
        <Input
          name="password"
          type="password"
          label="新しいパスワード"
          required
          helperText={`半角英数記号8文字以上24文字以内で、英字・数字・記号のうち2種類以上を混在させてください。\n
          使用可能な記号 ! " # $ % & ' ( ) * + , - . / : ; < = > ? @ [ \\ ] ^ _ \` { | } ~ \n
          ※スペースはご使用いただけません`}
          hasError={hasPasswordError}
          isNewPassword
          dataWovnIgnore={true}
        />
      </ContentWrapper>
      <ContentWrapper>
        <Input
          name="passwordConfirmation"
          type="password"
          label="確認のためパスワードを再入力してください"
          required
          hasError={hasPasswordError}
          isNewPassword
          dataWovnIgnore={true}
        />
      </ContentWrapper>

      <ButtonWrapper>
        <Button type="submit" text="変更" btnStyle="primary" rounded="md" />
      </ButtonWrapper>
    </FormTemplate>
  );
};

const ContentWrapper = styled.div`
  margin: 0 0 32px;
  input {
    font-weight: 400;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 48px 0 12px;
  button {
    font-family: "Hiragino Sans";
    height: auto;
    line-height: 1;
    font-weight: 500;
  }
`;
